import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import React, { useEffect, useState } from 'react'
import { Grid, Stack, Tooltip } from '@mui/material'
import { template } from 'lodash'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { push } from '@lagunovsky/redux-react-router'
import { SANDRE, nbPerPageLabelTiny } from 'referencial/constants/ReferencialConstants'
import { StyledFieldSet, StyledLegend, StyledSpan } from 'components/StyledElements'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import Button from 'components/forms/Button'
import ToastrAction from 'toastr/actions/ToastrAction'
import JobAction from 'import/actions/JobAction'
import { getObjectLabel } from 'utils/StoreUtils'
import { getSandreLabel } from 'utils/StringUtil'
import { getJobList } from 'utils/JobUtils'
import ReferencialAction from 'referencial/action/ReferencialAction'
import { JOB_TYPES, QUALITO_IMPORT_TYPE } from 'quality/components/qualitometers/addQualito/AddQualitoConstants'
import { hasValue } from 'utils/NumberUtil'
import { PLUVIO_IMPORT_TYPE } from 'pluviometry/constants/PluviometryConstant'
import { ALIGN, CardTable } from 'components/datatable/NewTable'
import AndroidIcon from '@mui/icons-material/Android'

const getNumberList = max => [...Array(max)].map((_, i) => ({ value: `${i}`, label: i > 9 ? i : `0${i}` }))
const getHours = () => [ { value: '*', label: i18n.everyHours }, ...getNumberList(24) ]
const getMinutes = () => [
    { value: '*/5', label: template(i18n.everyCustomMinutes)({ minute: 5 }) },
    { value: '*/10', label: template(i18n.everyCustomMinutes)({ minute: 10 }) },
    { value: '*/15', label: template(i18n.everyCustomMinutes)({ minute: 15 }) },
    { value: '*/20', label: template(i18n.everyCustomMinutes)({ minute: 20 }) },
    { value: '*/30', label: template(i18n.everyCustomMinutes)({ minute: 30 }) },
    ...getNumberList(60),
]
const getDays = () => [
    { value: '*', label: i18n.allDays },
    { value: '1', label: i18n.monday },
    { value: '2', label: i18n.tuesday },
    { value: '3', label: i18n.wednesday, disabled: true },
    { value: '4', label: i18n.thursday },
    { value: '5', label: i18n.friday },
    { value: '6', label: i18n.saturday },
    { value: '0', label: i18n.sunday },
]

const AddStepJob = ({
    station,
    stationType = 'quality',
    allStations = [],
    filters = {},
}) => {
    const dispatch = useDispatch()

    const {
        sandreCodes,
        citiesIndex,
        jobTable,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
        citiesIndex: store.CityReducer.citiesIndex,
        jobTable: store.JobReducer.jobTable,
    }), shallowEqual)

    const initialJob = { ...station.job, cron: '0 0 0 * * * *', days: '*', hours: '0', minutes: '0', id: null, name: null }

    const [newJob, setNewJob] = useState(initialJob)
    const [jobsWithStation, setJobsWithStation] = useState([])
    const changeJob = (changes) => setNewJob({ ...newJob, ...changes })

    const jobTableCriterias = {
        jobType: station.stationType === 'tidegauge'
            ? 'tidegauge'
            : JOB_TYPES[station.stationType],
        forceLoad: true,
        scheduled: true,
        displayMode: 'job',
    }

    const changePlanification = ({ minutes, hours, days }) => {
        const cron = ['0', minutes, hours, '*', '*', days, '*'].join(' ')
        changeJob({ cron, minutes, hours, days })
    }

    const handleChangeTime = (k, v, job) => {
        if (k === 'minutes' && v.includes('/')) {
            changePlanification({ ...job, minutes: v, hours: '*', days: '*' })
        } else if (k === 'hours' && v.includes('/')) {
            changePlanification({ ...job, hours: v, days: '*' })
        } else {
            changePlanification({ ...job, [k]: v })
        }
    }

    const handleChangeDays = (selected, job) => {
        changePlanification({ ...job, days: hasValue(selected) && selected != -1 ? selected.toString() : '*' })
    }

    const getCode = () => {
        if (station.stationType === PLUVIO_IMPORT_TYPE.METEO_FRANCE) {
            switch (filters.codeMode) {
                case 'cities': return filters.cityCodes[0]
                case 'departments': return filters.departmentCodes[0]
                default: return filters.stationCodes[0]
            }
        } else if (station.stationType === QUALITO_IMPORT_TYPE.OROBNAT) {
            return filters.codes[0]
        }
        return station.code ?? station.ids?.[0]
    }

    const getDataType = () => {
        switch (station.stationType) {
            case PLUVIO_IMPORT_TYPE.METEO_FRANCE: return filters.codeMode
            case QUALITO_IMPORT_TYPE.OROBNAT: return 'codes'
            default: return null
        }
    }

    const fetchHasStation = () => {
        return JobAction.jobHasStation(getCode(), getDataType()).then(json => setJobsWithStation(json))
    }

    const addJob = () => {
        if (!hasValue(newJob.name)) {
            dispatch(ToastrAction.warning(i18n.pleaseCompleteAllField))
        } else {
            dispatch(JobAction.addJob(newJob, false)).then(() => {
                setNewJob(initialJob)
                dispatch(JobAction.fetchJobTable(jobTableCriterias))
                fetchHasStation()
            })
        }
    }

    useEffect(() => {
        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }
        dispatch(JobAction.fetchJobTable(jobTableCriterias))
        fetchHasStation()
    }, [])

    const stations = station.ids?.length
        ? station.ids
            .map(i => allStations.find(q => q.id === i))
            .filter(Boolean)
            .map(s => ({
                id: s.id,
                code: { value: s.code },
                name: { value: s.name },
                nature: { value: getSandreLabel(sandreCodes, SANDRE.PIEZOMETER_NATURE, station.nature) },
                city: { value: getObjectLabel(citiesIndex[s.townCode], 'labelWithCode') },
                stationType: { value: s.stationType },
            }))
        : station.code
            ? [{
                id: allStations.find(q => q.code === station.code)?.id,
                code: { value: station.code },
                name: { value: allStations.find(q => q.code === station.code)?.name },
                stationType: { value: station.stationType },
            }]
            : []

    const jobList = getJobList(jobTable.data).jobs.map(j => {
        const jobHasStation = jobsWithStation.some(job => j.id === job.id)
        const { programmation, ...rest } = j
        return {
            ...rest,
            programmation: {
                value: (
                    <Stack spacing={1} direction='row'>
                        <Tooltip title={i18n.jobScheduled}>
                            <AndroidIcon/>
                        </Tooltip>
                        {programmation.value}
                    </Stack>
                ),
                sortValue: programmation.value,
            },
            lastExecution: { value: j.date.value },
            jobHasStation,
        }
    })

    return (
        <Stack spacing={1}>
            <CardTable
                title={i18n.importedData}
                rows={stations}
                headers={['code', 'name', 'city']}
                lineActions={[
                    {
                        align: ALIGN.RIGHT,
                        icon: 'border_color',
                        color: '#2196F3',
                        tooltip: i18n.accessDescription,
                        onClick: ({ id }) => {
                            dispatch(push(`/station/${stationType}/${id}/description`))
                        },
                    },
                    {
                        align: ALIGN.RIGHT,
                        icon: 'arrow_forward',
                        color: '#2196F3',
                        tooltip: i18n.accessToTheFile,
                        onClick: ({ id }) => {
                            dispatch(push(`/station/${stationType}/${id}/dashboard`))
                        },
                    },
                ]}
                maxHeight='62vh'
                rowsPerPageOptions={nbPerPageLabelTiny}
            />
            <CardTable
                title={i18n.jobs}
                rows={jobList}
                headers={['jobName', 'status', 'lastExecution', 'programmation']}
                rowsPerPageOptions={nbPerPageLabelTiny}
                lineActions={[
                    {
                        icon: ({ jobHasStation }) => jobHasStation ? 'location_on' : 'location_off',
                        align: ALIGN.RIGHT,
                        color: 'rgba(0, 0, 0, 0.87)',
                        tooltip: ({ jobHasStation }) => jobHasStation ? i18n.stationIsIn : i18n.stationIsOut,
                    },
                    {
                        icon: ({ jobHasStation }) => jobHasStation ? 'clear' : 'note_add',
                        align: ALIGN.RIGHT,
                        color: '#2196F3',
                        tooltip: ({ jobHasStation }) => jobHasStation ? i18n.removeStationFromJob : i18n.addStationToJob,
                        onClick: ({ jobHasStation, id }) => jobHasStation ?
                            JobAction.removeStationFromJob(getCode(), id, getDataType()).then(() => fetchHasStation().then(() => {
                                dispatch(ToastrAction.success(i18n.stationHasBeenRemoved))
                            })) :
                            JobAction.addStationToJob(getCode(), id, getDataType()).then(() => fetchHasStation().then(() => {
                                dispatch(ToastrAction.success(i18n.stationHasBeenAdd))
                            })),
                    },
                ]}
            />
            <StyledFieldSet>
                <StyledLegend>
                    <StyledSpan>{i18n.addJob}</StyledSpan>
                </StyledLegend>
                <Grid container spacing={1}>
                    <Grid size={12}>
                        <Input title={i18n.name} value={newJob.name} onChange={v => changeJob({ name: v })} />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            value={ newJob.hours || 0 }
                            options={ getHours() }
                            label={ i18n.jobHour }
                            onChange={ v => handleChangeTime('hours', v, newJob, changeJob) }
                            noNullValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            value={ newJob.minutes || 0 }
                            options={ getMinutes() }
                            label={ i18n.jobMinute }
                            onChange={ v => handleChangeTime('minutes', v, newJob, changeJob) }
                            noNullValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            value={ newJob.days }
                            options={ getDays() }
                            label={ i18n.jobDay }
                            onChange={ v => handleChangeDays(v, newJob, changeJob) }
                            noNullValue
                        />
                    </Grid>
                    <Grid size={3} justifyContent='center' alignItems='center'>
                        <Button title={i18n.add} icon='note_add' onClick={() => addJob(getCode())}/>
                    </Grid>
                </Grid>
            </StyledFieldSet>
        </Stack>
    )
}

AddStepJob.propTypes = {
    station: PropTypes.shape({
        code: PropTypes.string,
        stationType: PropTypes.string,
        name: PropTypes.string,
        jobExecutionId: PropTypes.number,
        job: PropTypes.shape({
            id: PropTypes.number,
        }),
        jobExecution: PropTypes.shape({
            statusCode: PropTypes.number,
        }),
        ids: PropTypes.arrayOf(PropTypes.number),
        nature: PropTypes.string,
    }),
    stationType: PropTypes.string,
    allStations: PropTypes.arrayOf(PropTypes.object),
    filters: PropTypes.shape({}),
}

export default AddStepJob